import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled from 'styled-components';

import { AppContext } from '../../context/AppContext';
import withPageTitle from '../../hocs/withPageTitle';
import type { ChartDataItem } from '../../types/charts';
import { OptimiseUserDetails } from '../../types/optimise';
import ChartCard from '../../ui/Dashboard/Finances/ChartCard';
import EarningsAccordion from '../../ui/Dashboard/Finances/EarningsAccordion';
import MonthSummary from '../../ui/Dashboard/Finances/MonthSummary';
import PeriodTabs from '../../ui/Dashboard/Finances/PeriodTabs';
import TariffPanel from '../../ui/Dashboard/Finances/TariffPanel';
import LoadingCard from '../../ui/LoadingCard';
import { UserSub } from '../../utils/apiRequest';
import { formatDate, formatISODate, subDays, subMonths } from '../../utils/dateUtils';
import {
  aggregateRangeItems,
  buildPeriodBars,
  fetchAnalyticsDay,
  fetchAnalyticsRange,
  FinancesPeriod,
  FinancialAggregate,
  getPeriodDateRange,
  PeriodBar,
} from '../../utils/financesUtils';
import { transformAnalyticsDataForChart } from '../../utils/savingsUtils';

interface AppContextType {
  userSub: UserSub;
  userDetails: OptimiseUserDetails | null;
}

const typedAppContext = AppContext as React.Context<AppContextType>;

const SectionBox = styled.div`
  border: 1px solid ${(props) => props.theme.colors.light};
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 24px;
  background: ${(props) => props.theme.colors.white};
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: bold;
  color: ${(props) => props.theme.colors.dark};
  margin-top: 30px;
  margin-bottom: 8px;
`;

const DateLabel = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
  color: ${(props) => props.theme.colors.dark};
`;

const ChartDivider = styled.hr`
  margin: 24px 0;
  border-color: ${(props) => props.theme.colors.light};
`;

const DateSelector = styled.div`
  background-color: ${(props) => props.theme.colors.extraLight};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  cursor: pointer;
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__day--selected {
    background-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.white};

    &:hover {
      background-color: ${(props) => props.theme.colors.primaryHover};
    }
  }

  input {
    background: transparent;
    border: none;
    font-size: 16px;
    width: 100%;
    cursor: pointer;
    padding: 12px 16px;

    &:focus-visible {
      outline-color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 15px;
  pointer-events: none;
  z-index: 1;
`;

const FinancesContainer = () => {
  const { userSub, userDetails } = useContext(typedAppContext);

  const [period, setPeriod] = useState<FinancesPeriod>('day');
  const [anchorDate, setAnchorDate] = useState<Date>(subDays(new Date(), 1));

  // Day-view data (transformed for existing ExportChart / ImportChart)
  const [dayChartData, setDayChartData] = useState<ChartDataItem[] | null>(null);
  const [loadingDay, setLoadingDay] = useState(false);

  // Aggregated bars for week / month / year views
  const [periodBars, setPeriodBars] = useState<PeriodBar[] | null>(null);
  const [loadingPeriod, setLoadingPeriod] = useState(false);

  const tariffNames = useMemo(() => {
    const imp = userDetails?.tariffs?.import_tariff;
    const exp = userDetails?.tariffs?.export_tariff;
    if (!imp || !exp) return null;
    return { import: imp.tariff_name, export: exp.tariff_name };
  }, [userDetails]);

  // Monthly summaries (top of page)
  const [currentMonthAggregate, setCurrentMonthAggregate] = useState<FinancialAggregate | null>(null);
  const [lastMonthAggregate, setLastMonthAggregate] = useState<FinancialAggregate | null>(null);
  const [loadingMonthly, setLoadingMonthly] = useState(true);

  // ── Monthly summaries ──────────────────────────────────────────────────
  useEffect(() => {
    const today = new Date();
    const lastMonth = subMonths(today, 1);

    const fetchMonthly = async () => {
      setLoadingMonthly(true);
      try {
        const thisRange = getPeriodDateRange(today, 'month');
        const lastRange = getPeriodDateRange(lastMonth, 'month');
        const [thisMonthRes, lastMonthRes] = await Promise.all([
          fetchAnalyticsRange('month', thisRange.startDate, thisRange.endDate, userSub),
          fetchAnalyticsRange('month', lastRange.startDate, lastRange.endDate, userSub),
        ]);
        setCurrentMonthAggregate(aggregateRangeItems(thisMonthRes.analytics));
        setLastMonthAggregate(aggregateRangeItems(lastMonthRes.analytics));
      } catch {
        // Monthly summaries are non-critical; leave null
      } finally {
        setLoadingMonthly(false);
      }
    };

    void fetchMonthly();
  }, [userSub]);

  // ── Day-view chart data ────────────────────────────────────────────────
  useEffect(() => {
    if (period !== 'day') return;
    setLoadingDay(true);
    setDayChartData(null);

    const dateStr = formatISODate(anchorDate);
    fetchAnalyticsDay(dateStr, userSub)
      .then((data) => setDayChartData(transformAnalyticsDataForChart(data) as ChartDataItem[]))
      .catch(() => setDayChartData([]))
      .finally(() => setLoadingDay(false));
  }, [period, anchorDate, userSub]);

  // ── Aggregated period data (week / month / year) ───────────────────────
  useEffect(() => {
    if (period === 'day') return;
    setLoadingPeriod(true);
    setPeriodBars(null);

    const fetchAndBuild = async () => {
      try {
        const { startDate, endDate } = getPeriodDateRange(anchorDate, period);
        const res = await fetchAnalyticsRange(period, startDate, endDate, userSub);
        setPeriodBars(buildPeriodBars(res.analytics, period, anchorDate));
      } catch {
        setPeriodBars([]);
      } finally {
        setLoadingPeriod(false);
      }
    };

    void fetchAndBuild();
  }, [period, anchorDate, userSub]);

  // ── Derived totals for the chart card header ───────────────────────────
  const { exportTotal, importTotal } = useMemo(() => {
    if (period === 'day') {
      if (!dayChartData) return { exportTotal: 0, importTotal: 0 };
      const exp = dayChartData.reduce((acc, row) => acc + row.actual_export_earning, 0);
      const imp = -dayChartData.reduce((acc, row) => acc + row.actual_import_cost, 0);
      return { exportTotal: exp, importTotal: imp };
    }
    if (!periodBars) return { exportTotal: 0, importTotal: 0 };
    return {
      exportTotal: periodBars.reduce((acc, b) => acc + b.exportEarnings, 0),
      importTotal: periodBars.reduce((acc, b) => acc + b.importCosts, 0),
    };
  }, [period, dayChartData, periodBars]);

  const currentMonthLabel = formatDate(new Date(), 'MMMM');
  const lastMonthLabel = formatDate(subMonths(new Date(), 1), 'MMMM');
  const loading = period === 'day' ? loadingDay : loadingPeriod;

  const handlePeriodChange = useCallback((newPeriod: FinancesPeriod) => {
    setPeriod(newPeriod);
    // Reset anchor to a sensible default for the new period
    setAnchorDate(subDays(new Date(), 1));
  }, []);

  return (
    <div>
      {/* Monthly summaries */}
      {loadingMonthly ? (
        <div className="mb-4">
          <LoadingCard />
        </div>
      ) : (
        <MonthSummary
          currentMonthLabel={currentMonthLabel}
          lastMonthLabel={lastMonthLabel}
          currentMonth={currentMonthAggregate}
          lastMonth={lastMonthAggregate}
        />
      )}

      {/* Earnings explanation accordion */}
      <EarningsAccordion />

      {/* Chart section */}
      <SectionTitle>Total grid earnings and costs</SectionTitle>
      <SectionBox>
        <DateLabel>Date</DateLabel>
        <DateSelector>
          <DatePicker
            selected={anchorDate}
            onChange={(date: Date | null) => date && setAnchorDate(date)}
            dateFormat="dd/MM/yy"
            maxDate={subDays(new Date(), 1)}
            preventOpenOnFocus
          />
          <ChevronIcon icon={faChevronDown} />
        </DateSelector>
        <div className="mb-3">
          <PeriodTabs period={period} onChange={handlePeriodChange} />
        </div>

        {loading ? (
          <LoadingCard />
        ) : (
          <>
            <ChartCard
              section="import"
              period={period}
              anchorDate={anchorDate}
              onNavigate={setAnchorDate}
              dayChartData={dayChartData}
              periodBars={periodBars}
              exportTotal={exportTotal}
              importTotal={importTotal}
            />
            <ChartDivider />
            <ChartCard
              section="export"
              period={period}
              anchorDate={anchorDate}
              onNavigate={setAnchorDate}
              dayChartData={dayChartData}
              periodBars={periodBars}
              exportTotal={exportTotal}
              importTotal={importTotal}
            />
          </>
        )}
      </SectionBox>

      {/* Tariff panel – uses day-view transformed data for rate categories */}
      <TariffPanel chartData={dayChartData} tariffNames={tariffNames} />
    </div>
  );
};

export default function WrappedFinancesContainer(): React.ReactElement {
  return withPageTitle('Finances', FinancesContainer)();
}
